import { Injectable } from '@angular/core';
import { Observable, map, of, tap, forkJoin, throwError } from 'rxjs';
import { CacheService } from './cache.service';
import { ReleaseReportService } from './releasereport.service';
import { UserProfileService } from './user-profile.service';
import { EnvironmentService } from './environment.service';
import { ReleaseReportReleasesResponse } from '../models/release';
import { OwnershipEntry } from '../models/ownership-entry';
import { Environment } from '../models/environment';
import { PrTimeToFirstReviewResult, TeamMetricResult, CalculationLogic, MetricType } from '../models/team-metrics';
import { AdoPullRequestSearchCriteria } from '../models/ado-pull-request-search-criteria';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TeamMetricsService {
  private readonly bulkDataCacheKey = 'team_metrics_bulk_release_data';
  private readonly bulkDataCacheDuration = 1 * 60 * 60 * 1000;
  
  private ownershipData: OwnershipEntry[] = [];
  private bulkReleaseData: ReleaseReportReleasesResponse[] = [];
  private bulkDataRange: { start: Date, end: Date } | null = null;
  private readonly environment: Environment;

  /**
   * Constructs the TeamMetricsService. This service is responsible for managing shared data
   * (like bulk release data and ownership information), providing common utility functions
   * for metric calculations, and handling generic comparison logic for metrics.
   * @param cacheService Service for caching data.
   * @param releaseReportService Service for fetching release report data.
   * @param userProfileService Service for user profile information.
   * @param environmentService Service for environment-specific configurations.
   */
  constructor(
    private readonly cacheService: CacheService,
    private readonly releaseReportService: ReleaseReportService,
    private readonly userProfileService: UserProfileService,
    private readonly environmentService: EnvironmentService
    ) {
    this.environment = this.environmentService.getEnvironment();
    this.loadBulkDataFromCache();
  }

  /**
   * Initializes or retrieves cached bulk release data for a given period.
   * Fetches data from the API if not present in the cache or if the cached range doesn't cover the requested period.
   * This data is used by various metric calculator services.
   * @param startDate The start date for the bulk data period in ISO format (UTC).
   * @param endDate The end date for the bulk data period in ISO format (UTC).
   * @returns An Observable that completes when bulk data is initialized or loaded from cache.
   */
  public initializeBulkReleaseData(startDate: string, endDate: string): Observable<void> {
    const cachedBulkData = this.cacheService.get<any>(this.bulkDataCacheKey, this.bulkDataCacheDuration);
    if (cachedBulkData?.releases && cachedBulkData.range) {
      const cachedRangeStart = new Date(cachedBulkData.range.start);
      const cachedRangeEnd = new Date(cachedBulkData.range.end);
      const requestedStart = new Date(startDate);
      const requestedEnd = new Date(endDate);

      if (requestedStart >= cachedRangeStart && requestedEnd <= cachedRangeEnd) {
        this.bulkReleaseData = cachedBulkData.releases;
        this.bulkDataRange = {
          start: cachedRangeStart,
          end: cachedRangeEnd
        };
        return of(void 0);
      }
    }

    return this.releaseReportService.getReleases(startDate, endDate).pipe(
      tap(releases => {
        this.bulkReleaseData = releases;
        this.bulkDataRange = { start: new Date(startDate), end: new Date(endDate) };
        
        this.cacheService.set(this.bulkDataCacheKey, {
          releases: this.bulkReleaseData,
          range: this.bulkDataRange
        });
      }),
      map(() => void 0)
    );
  }

  /**
   * Loads bulk release data from the local storage cache into service memory if available and valid.
   */
  private loadBulkDataFromCache(): void {
    const cachedBulkData = this.cacheService.get<any>(this.bulkDataCacheKey, this.bulkDataCacheDuration);
    if (cachedBulkData?.releases && cachedBulkData.range) {
      this.bulkReleaseData = cachedBulkData.releases;
      this.bulkDataRange = {
        start: new Date(cachedBulkData.range.start),
        end: new Date(cachedBulkData.range.end)
      };
    }
  }

  /**
   * Retrieves release data for a specified date range, utilizing cached bulk data if possible.
   * If the requested range is within the cached bulk data, filtered data is returned.
   * Otherwise, it falls back to a direct API call via `ReleaseReportService`.
   * This method is used by metric calculator services to obtain release information.
   * @param requestedStartDate The start date of the desired range in ISO format (UTC).
   * @param requestedEndDate The end date of the desired range in ISO format (UTC).
   * @returns An Observable emitting an array of `ReleaseReportReleasesResponse`.
   */
  public getSmartReleaseData(requestedStartDate: string, requestedEndDate: string): Observable<ReleaseReportReleasesResponse[]> {
    const requestStart = new Date(requestedStartDate);
    const requestEnd = new Date(requestedEndDate);
    
    if (this.isWithinBulkRange(requestStart, requestEnd)) {
      const filteredData = this.filterBulkDataByRange(requestStart, requestEnd);
      return of(filteredData);
    }
    
    return this.releaseReportService.getReleases(requestedStartDate, requestedEndDate);
  }

  /**
   * Checks if a given date range is fully contained within the currently cached bulk data range.
   * @param start The start date of the range to check.
   * @param end The end date of the range to check.
   * @returns True if the range is within the bulk data, false otherwise.
   */
  private isWithinBulkRange(start: Date, end: Date): boolean {
    return !!this.bulkDataRange &&
           this.bulkReleaseData.length > 0 &&
           start >= this.bulkDataRange.start &&
           end <= this.bulkDataRange.end;
  }

  /**
   * Filters the in-memory bulk release data to a specific date range.
   * @param start The start date for filtering.
   * @param end The end date for filtering.
   * @returns An array of release responses filtered by the given date range.
   */
  private filterBulkDataByRange(start: Date, end: Date): ReleaseReportReleasesResponse[] {
    if (!this.bulkReleaseData) {
      return [];
    }
    return this.bulkReleaseData.filter(release => {
      const releaseDate = new Date(release.Release.StartedDateTimeUtc);
      return releaseDate >= start && releaseDate <= end;
    });
  }

  /**
   * Calculates the equivalent previous period for comparison.
   * @param startDate Start date of current period in ISO format.
   * @param endDate End date of current period in ISO format.
   * @returns Object with previous period start and end dates.
   */
  private calculatePreviousPeriod(startDate: string, endDate: string): { prevStart: string, prevEnd: string } {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    
    const prevEnd = new Date(start.getTime() - 1);
    
    const prevStart = new Date(prevEnd.getTime());
    prevStart.setUTCDate(prevStart.getUTCDate() - (diffDays - 1));
    prevStart.setUTCHours(0, 0, 0, 0);

    return {
      prevStart: prevStart.toISOString(),
      prevEnd: prevEnd.toISOString()
    };
  }

  /**
   * Calculates percentage change between current and previous values.
   * @param current Current period value.
   * @param previous Previous period value.
   * @returns Percentage change (positive = increase, negative = decrease).
   */
  private calculatePercentageChange(current: number, previous: number): number {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  }

  /**
   * Formats the comparison period text for display.
   * @param startDate Start date in ISO format.
   * @param endDate End date in ISO format.
   * @returns Formatted comparison period string (e.g., "vs previous 30 days").
   */
  private formatComparisonPeriod(startDate: string, endDate: string): string {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return `vs previous ${days} days`;
  }

  /**
   * Adds comparison data (previous value, percentage change, comparison period text) to a metric result.
   * It calculates the metric for the current period and the equivalent previous period, then combines them.
   * This is a generic utility used by various metric calculator services.
   * @template T The type of the metric result, extending `TeamMetricResult`.
   * @param metricCalculator A function that takes team name, start date, end date, and optional calculation logic, and returns an Observable of the metric result.
   * @param teamName The team name for the metric calculation.
   * @param startDate Start date of the current period in ISO format (UTC).
   * @param endDate End date of the current period in ISO format (UTC).
   * @param includeComparison Whether to include comparison data. If false, only the current metric is returned.
   * @param metricType The type of the metric (from `MetricType`), used for logging and specific handling (e.g., PR Time).
   * @param calculationLogic Optional calculation logic for specific metrics (e.g., 'Mean', 'Median', 'IQR' for PR Time to First Review).
   * @returns An Observable of the metric result, augmented with comparison data if `includeComparison` is true.
   */
  public addComparisonToMetric<T extends TeamMetricResult>(
    metricCalculator: (teamName: string, startDate: string, endDate: string, calculationLogic?: CalculationLogic) => Observable<T>,
    teamName: string,
    startDate: string,
    endDate: string,
    includeComparison: boolean,
    metricType: MetricType,
    calculationLogic?: CalculationLogic
  ): Observable<T> {
    const currentMetric$ = calculationLogic 
      ? metricCalculator(teamName, startDate, endDate, calculationLogic)
      : metricCalculator(teamName, startDate, endDate);
    
    if (!includeComparison) {
      return currentMetric$;
    }
    
    const { prevStart, prevEnd } = this.calculatePreviousPeriod(startDate, endDate);
    
    const previousMetric$ = calculationLogic
      ? metricCalculator(teamName, prevStart, prevEnd, calculationLogic)
      : metricCalculator(teamName, prevStart, prevEnd);
    
    return forkJoin([currentMetric$, previousMetric$]).pipe(
      map(([current, previous]) => {
        const percentageChange = this.calculatePercentageChange(current.value, previous.value);

        const metricDisplayNames: { [key in MetricType]?: string } = {
          prTime: 'PR Time to First Review',
          changeFailRate: 'Change Fail Rate',
          deploymentFrequency: 'Deployment Frequency'
        };

        const logDisplayName = metricDisplayNames[metricType] ?? metricType;

        console.log(`[${logDisplayName}] Current value: ${current.value}`);
        console.log(`[${logDisplayName}] Previous value: ${previous.value}`);
        console.log(`[${logDisplayName}] Percentage change: ${percentageChange.toFixed(1)}%`);
        console.log(`[${logDisplayName}] ---`);
        
        let finalResultObject = {
          ...current,
          previousValue: previous.value,
          percentageChange: percentageChange,
          comparisonPeriod: this.formatComparisonPeriod(startDate, endDate),
        };

        if (metricType === 'prTime') {
          const specificResult = finalResultObject as any as PrTimeToFirstReviewResult;
          const previousPrResult = previous as any as PrTimeToFirstReviewResult;

          if (previousPrResult?.rawDurationsHours) {
            specificResult.previousPeriodDataForRecalc = {
              rawDurationsHours: previousPrResult.rawDurationsHours || [],
              totalPrsConsidered: previousPrResult.totalPrsConsidered,
              totalPrsFetched: previousPrResult.totalPrsFetched,
              startDateIso: prevStart, 
              endDateIso: prevEnd     
            };
          }
        }
        return finalResultObject as T;
      })
    );
  }

  /**
   * Filters an array of release responses to include only those associated with a specific team,
   * based on ownership data (pipeline to team mapping).
   * This utility is used by metric calculator services.
   * @param releases An array of `ReleaseReportReleasesResponse` objects.
   * @param teamName The name of the team to filter by.
   * @returns A new array containing only the releases associated with the specified team.
   */
  public filterReleasesByTeam(releases: ReleaseReportReleasesResponse[], teamName: string): ReleaseReportReleasesResponse[] {
    const filteredReleases = releases.filter(release => {
      const pipelineName = release.Release.PipelineName;
      const isYaml = release.Release.IsYaml;
      const releaseTeam = this.getTeamForPipeline(pipelineName, isYaml);
      return releaseTeam === teamName;
    });
    return filteredReleases;
  }

  /**
   * Gets the team name for a pipeline using ownership data.
   * @param pipelineName Name of the pipeline.
   * @param isYaml Whether the pipeline is YAML-based.
   * @returns Team name or empty string if not found.
   */
  private getTeamForPipeline(pipelineName: string, isYaml: boolean): string {
    const ownership: OwnershipEntry[] = this.ownershipData || [];
    const propertyGroup = isYaml ? 'yamlPipelines' : 'releaseDefinitions';

    const result = ownership
      .filter((ownershipEntry) =>
        ownershipEntry[propertyGroup]?.some(
          (pipeline: { name: string }) => pipeline.name === pipelineName
        )
      )
      .map((ownershipEntry) => ownershipEntry.team);

    return result.length > 0 ? result[0] : '';
  }

  /**
   * Ensures that ownership data (mapping repositories/pipelines to teams) is loaded.
   * If data is already in memory, it returns it as an Observable. Otherwise, it fetches
   * the data using `UserProfileService` and caches it in memory.
   * This is crucial for metric calculators that need to filter data by team.
   * @returns An Observable emitting an array of `OwnershipEntry` objects.
   */
  public ensureOwnershipDataLoaded(): Observable<OwnershipEntry[]> {
    if (this.ownershipData.length > 0) {
      return of(this.ownershipData);
    }
    
    const defaultOrgName = this.environment.defaultOrg;
    return this.userProfileService.getRepoOwnershipMaster(defaultOrgName).pipe(
      tap((ownershipData: OwnershipEntry[]) => {
        this.ownershipData = ownershipData;
      }),
      catchError(error => {
        console.error('Error in ensureOwnershipDataLoaded():', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Creates an `AdoPullRequestSearchCriteria` object for querying pull requests
   * within a specific date range.
   * This utility is used by services that fetch pull request data (e.g., `PrTimeToReviewCalculatorService`).
   * @param startDate The start date for the search criteria in ISO format (UTC).
   * @param endDate The end date for the search criteria in ISO format (UTC).
   * @returns An `AdoPullRequestSearchCriteria` object configured for the given date range.
   */
  public createPullRequestSearchCriteria(startDate: string, endDate: string): AdoPullRequestSearchCriteria {
    return {
      'searchCriteria.minTime': startDate,
      'searchCriteria.maxTime': endDate,
      'searchCriteria.status': 'all',
      'searchCriteria.queryTimeRangeType': 'created',
      'searchCriteria.includeLinks': false
    };
  }

  /**
   * Calculates the median (middle value) of an array of numbers.
   * Sorts the numbers and finds the middle element or average of the two middle elements.
   * @param numbers An array of numbers.
   * @returns The median value, or `null` if the input array is empty.
   */
  public calculateMedian(numbers: number[]): number | null {
    if (numbers.length === 0) return null;
    const sorted = [...numbers].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  }

  /**
   * Calculates the mean (average) of an array of numbers.
   * Sums all numbers and divides by the count.
   * @param numbers An array of numbers.
   * @returns The mean value, or `null` if the input array is empty.
   */
  public calculateMean(numbers: number[]): number | null {
    if (numbers.length === 0) return null;
    return numbers.reduce((acc, val) => acc + val, 0) / numbers.length;
  }

  /**
   * Calculates the Interquartile Range (IQR), Q1 (first quartile), and Q3 (third quartile)
   * for an array of numbers. IQR is a measure of statistical dispersion.
   * @param numbers An array of numbers.
   * @returns An object containing `q1`, `q3`, and `iqr` values, or `null` if the input array has fewer than 2 elements.
   */
  public calculateIQR(numbers: number[]): { q1: number; q3: number; iqr: number } | null {
    if (numbers.length < 2) return null;

    const sorted = [...numbers].sort((a, b) => a - b);
    
    const getQuantile = (arr: number[], q: number): number => {
      const pos = (arr.length - 1) * q;
      const base = Math.floor(pos);
      const rest = pos - base;
      if (arr[base + 1] !== undefined) {
        return arr[base] + rest * (arr[base + 1] - arr[base]);
      } else {
        return arr[base];
      }
    };

    const q1 = getQuantile(sorted, 0.25);
    const q3 = getQuantile(sorted, 0.75);

    return {
      q1: q1,
      q3: q3,
      iqr: q3 - q1,
    };
  }


  /**
   * Formats a date range into a display string "YYYY-MM-DD - YYYY-MM-DD", using UTC dates.
   * This utility is used for displaying periods in metric results.
   * @param startDate The start date of the range, as an ISO string (UTC).
   * @param endDate The end date of the range, as an ISO string (UTC).
   * @returns A string representing the formatted date range.
   */
  public formatDateRange(startDate: string, endDate: string): string {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const startYear = start.getUTCFullYear();
    const startMonth = (start.getUTCMonth() + 1).toString().padStart(2, '0');
    const startDay = start.getUTCDate().toString().padStart(2, '0');
    
    const endYear = end.getUTCFullYear();
    const endMonth = (end.getUTCMonth() + 1).toString().padStart(2, '0');
    const endDay = end.getUTCDate().toString().padStart(2, '0');

    const startStr = `${startYear}-${startMonth}-${startDay}`;
    const endStr = `${endYear}-${endMonth}-${endDay}`;
    
    return `${startStr} - ${endStr}`;
  }

}
